import { motion } from "framer-motion";
import { useState } from "react";
import { Calendar, User, Note, Trash, ArrowRight, CheckCircle, Flag, Paperclip, Warning } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import TaskDeliverableUpload from "./TaskDeliverableUpload";
import ReopenTaskDialog from "./ReopenTaskDialog";

const priorityStyles = {
  high: { border: "border-l-orange-500", label: "bg-orange-100 text-orange-800" },
  medium: { border: "border-l-blue-600", label: "bg-blue-100 text-blue-800" },
  low: { border: "border-l-zinc-400", label: "bg-zinc-200 text-zinc-700" },
};

const CARD_INITIAL = { opacity: 0, y: 8 };
const CARD_ANIMATE = { opacity: 1, y: 0 };
const CARD_EXIT = { opacity: 0, scale: 0.95 };
const CARD_HOVER = { y: -2 };
const CARD_TRANSITION = { duration: 0.15, ease: "easeOut" };

const MS_48H = 48 * 60 * 60 * 1000;

/**
 * Returns { state, label, hoursLeft } for a task's due date.
 * state: 'overdue' | 'urgent' | 'ok' | 'none'
 * Only applies when task is not complete.
 */
function computeDueUrgency(task) {
  if (!task.due_date || task.status === "complete") return { state: "none" };
  const due = new Date(task.due_date);
  if (isNaN(due.getTime())) return { state: "none" };
  // treat due_date as end-of-day
  const dueEnd = new Date(due);
  dueEnd.setHours(23, 59, 59, 999);
  const now = new Date();
  const diff = dueEnd.getTime() - now.getTime();
  if (diff < 0) {
    const days = Math.ceil(Math.abs(diff) / (24 * 60 * 60 * 1000));
    return { state: "overdue", label: `Overdue by ${days} day${days === 1 ? "" : "s"}`, diff };
  }
  if (diff <= MS_48H) {
    const hoursLeft = Math.max(1, Math.round(diff / (60 * 60 * 1000)));
    return { state: "urgent", label: `Due in ${hoursLeft}h`, hoursLeft, diff };
  }
  return { state: "ok", diff };
}

export default function TaskCard({ task, isAdmin, me, onChangeStatus, onDelete, onEdit, onRefresh }) {
  const [openDeliverable, setOpenDeliverable] = useState(false);
  const [openReopen, setOpenReopen] = useState(false);
  const p = priorityStyles[task.priority] || priorityStyles.medium;
  const due = task.due_date ? new Date(task.due_date) : null;
  const dueStr = due ? due.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null;
  const urgency = computeDueUrgency(task);
  const isUrgent = urgency.state === "urgent" || urgency.state === "overdue";
  const urgentColor = urgency.state === "overdue" ? "bg-red-600" : "bg-red-500";
  const dueTextColor = urgency.state === "overdue" ? "text-red-700 font-bold"
                        : urgency.state === "urgent" ? "text-red-600 font-semibold" : "";
  // Only the workspace admin OR the task's own assignee may transition its status.
  const canChangeStatus = me?.role === "admin" || me?.role === "manager" || task.assignee_id === me?.id;
  const canReopen = me?.role === "admin" || me?.role === "manager";

  return (
    <motion.div
      layout
      initial={CARD_INITIAL}
      animate={CARD_ANIMATE}
      exit={CARD_EXIT}
      whileHover={CARD_HOVER}
      transition={CARD_TRANSITION}
      data-testid={`task-card-${task.id}`}
      data-urgency={urgency.state}
      className={`group bg-white border-y border-r border-zinc-300 border-l-4 ${p.border} rounded-none hover:shadow-brutal-sm`}
    >
      {isUrgent && (
        <div
          data-testid={`due-alert-${task.id}`}
          className={`${urgentColor} text-white px-3 py-1 flex items-center gap-1.5 label-overline`}
        >
          <Warning size={11} weight="bold" />
          {urgency.label}
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2 mb-1.5 flex-wrap" data-testid={`task-jobno-${task.id}`}>
              <span className="label-overline text-zinc-500">Job No.</span>
              <span className="font-display font-black text-xl sm:text-2xl tracking-tighter text-orange-600 leading-none">
                {task.project || "—"}
              </span>
              {task.client_name && (
                <span className="text-xs text-zinc-600 truncate max-w-full" data-testid={`task-client-${task.id}`}>
                  · {task.client_name}
                </span>
              )}
            </div>
            <h3 className="font-display font-bold text-base sm:text-lg text-zinc-900 tracking-tight leading-snug break-words">
              {task.title}
            </h3>
          </div>
          <span className={`shrink-0 label-overline px-2 py-1 ${p.label}`}>{task.priority}</span>
        </div>

        {task.is_milestone && (
          <div
            data-testid={`milestone-badge-${task.id}`}
            className="mt-3 inline-flex items-center gap-1.5 border border-orange-500 bg-orange-50 px-2 py-1"
          >
            <Flag size={11} weight="bold" className="text-orange-600" />
            <span className="label-overline text-orange-700">
              Milestone{task.milestone_amount ? ` · ₹${task.milestone_amount.toLocaleString("en-IN")}` : ""}
            </span>
          </div>
        )}

        {task.description && (
          <p className="mt-3 text-sm text-zinc-600 leading-snug line-clamp-3">{task.description}</p>
        )}

        <div className="mt-4 flex items-center gap-3 sm:gap-4 text-xs text-zinc-500 flex-wrap">
          <span className="flex items-center gap-1.5" title={task.assignee_email}>
            <User size={12} weight="bold" /> {task.assignee_name}
          </span>
          {dueStr && (
            <span className={`flex items-center gap-1.5 ${dueTextColor}`}>
              <Calendar size={12} weight="bold" /> {dueStr}
            </span>
          )}
          {task.notes && (
            <span className="flex items-center gap-1.5" title={task.notes}>
              <Note size={12} weight="bold" /> Notes
            </span>
          )}
          {task.references?.length > 0 && (
            <span className="flex items-center gap-1.5 text-blue-700"
                  title={`${task.references.length} reference file${task.references.length > 1 ? "s" : ""}`}>
              <Paperclip size={12} weight="bold" /> {task.references.length} ref
            </span>
          )}
        </div>

        {/* Actions per status */}
        <div className="mt-4 flex items-center gap-2">
          {task.status === "todo" && canChangeStatus && (
            <Button
              data-testid={`start-task-${task.id}`}
              onClick={() => onChangeStatus(task.id, "in_progress")}
              className="rounded-none h-8 bg-blue-600 hover:bg-blue-700 text-white text-xs font-mono uppercase tracking-widest"
            >
              Start Working <ArrowRight size={12} weight="bold" className="ml-1" />
            </Button>
          )}
          {task.status === "in_progress" && canChangeStatus && (
            <>
              {(task.deliverable_count ?? 0) > 0 ? (
                <Button
                  data-testid={`complete-task-${task.id}`}
                  onClick={() => onChangeStatus(task.id, "complete")}
                  className="rounded-none h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-mono uppercase tracking-widest"
                >
                  <CheckCircle size={12} weight="bold" className="mr-1" /> Complete
                </Button>
              ) : (
                <button
                  type="button"
                  data-testid={`complete-blocked-${task.id}`}
                  onClick={() => setOpenDeliverable(true)}
                  className="h-8 flex items-center px-3 border-2 border-amber-500 bg-amber-50 text-amber-800 hover:bg-amber-100 hover:border-amber-600 text-[10px] font-mono uppercase tracking-widest gap-1 cursor-pointer transition-colors"
                  title="Click to upload a deliverable and unlock Complete"
                >
                  <Paperclip size={11} weight="bold" /> Upload deliverable to complete
                </button>
              )}
              <Button
                data-testid={`revert-task-${task.id}`}
                onClick={() => onChangeStatus(task.id, "todo")}
                variant="outline"
                className="rounded-none h-8 border-zinc-400 text-xs font-mono uppercase tracking-widest hover:bg-zinc-100"
              >
                Pause
              </Button>
            </>
          )}
          {task.status === "complete" && canChangeStatus && (
            <Button
              data-testid={`reopen-task-${task.id}`}
              onClick={() => {
                // Admin / PM get the "set a new due date" dialog;
                // the assignee reopening their own task jumps straight to in_progress.
                if (canReopen) setOpenReopen(true);
                else onChangeStatus(task.id, "in_progress");
              }}
              variant="outline"
              className="rounded-none h-8 border-zinc-400 text-xs font-mono uppercase tracking-widest hover:bg-zinc-100"
            >
              Reopen
            </Button>
          )}

          {/* Deliverable upload dialog — mounted for all statuses so the "Upload deliverable to complete" button works on in-progress tasks too */}
          <TaskDeliverableUpload
            task={task}
            canDelete={isAdmin}
            open={openDeliverable}
            onOpenChange={setOpenDeliverable}
            hideTrigger={task.status !== "complete"}
          />

          {isAdmin && (
            <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                data-testid={`edit-task-${task.id}`}
                onClick={() => onEdit(task)}
                className="label-overline text-zinc-500 hover:text-blue-600 px-2 py-1"
              >
                Edit
              </button>
              <button
                data-testid={`delete-task-${task.id}`}
                onClick={() => onDelete(task)}
                className="text-zinc-500 hover:text-red-600 p-1"
                aria-label="Delete task"
              >
                <Trash size={14} weight="bold" />
              </button>
            </div>
          )}
        </div>
      </div>
      <ReopenTaskDialog
        task={task}
        open={openReopen}
        onOpenChange={setOpenReopen}
        onDone={() => onRefresh && onRefresh()}
      />
    </motion.div>
  );
}
